import React from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import * as serviceWorker from './serviceWorker'
import * as Sentry from '@sentry/react'
import { router } from 'routes'
import { config, missingConnectionConfig } from 'config'
import { initConsoleCapture } from 'helpers/diagnostics'

import 'style/tailwind.css'
import 'style/style.scss'

initConsoleCapture()

const version = import.meta.env.NODE_ENV === 'production' ? import.meta.env.REACT_APP_GIT_SHORT_HASH : 'development'
// REACT_APP_GIT_SHORT_HASH stays a build-time value — it identifies the build,
// not the environment.
//
// Not in E2E runs (ENVIRONMENT "test"): they serve the production bundle, and the errors their
// tests provoke on purpose would land in the real Sentry project.

if (import.meta.env.NODE_ENV === 'production' && config.ENVIRONMENT !== 'test') {
  Sentry.init({
    dsn: 'https://d06bc68ba3f44a18d8a409ea17eeab3d@o4509922251571200.ingest.de.sentry.io/4510358028025936',
    ignoreErrors: [
      'APIError:',
      'AbortError',
      'NotAllowedError',
      'Failed to fetch',
      'ResizeObserver',
      'Failed to fetch',
      'A valid "Authorization" token was not found in the request headers',
      'Your sign-in has expired or is missing',
      'network error',
      'Load failed',
      'The operation is insecure.',
      'NotAllowedError: Document is not focused',
      'Unable to send Replay - max retries exceeded',
      'NetworkError when attempting to fetch resource',
      'Flowable <',
      'The requested URL was not found on the server. If you entered the URL manually please check your spelling and try again',
      'You do not have the appropriate permissions to do that.',
      "Can't find variable: M",
      'UnhandledRejection: Non-Error promise rejection captured with value: Object Not Found Matching Id:',
      'antifingerprint not defined yet. will try and handle event after its ready',
      'You do not have access to this hub',
      'API key not found',
      'Hub not found',
      'Session expired – redirected to sign in',
      'Table is part of a deleted hub',
      "You don't have permission to do that.",
      "You don't have access to this hub.",
      "We couldn't find this hub.",
      'This table is in a deleted hub.',
      'An event processor returned `null`, will not send event.'
    ],
    environment: config.SENTRY_ENV || config.ENVIRONMENT,
    release: `web-app@${version}`,
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true
      })
    ],
    beforeSend(event) {
      // 1) First ignore ResizeObserver
      if (
        event &&
        event.message &&
        event.message.match &&
        typeof event.message.match === 'function' &&
        event.message.match(/ResizeObserver loop/i)
      ) {
        return null
      }

      // 2) Then check if any frame in the exception stacktrace starts with "chrome-extension"
      const exceptionValues = event?.exception?.values ?? []
      for (const exception of exceptionValues) {
        const frames = exception?.stacktrace?.frames ?? []
        for (const frame of frames) {
          if (frame?.filename?.includes('chrome-extension://')) {
            // Ignore this event entirely if it looks like a chrome-extension error
            return null
          }
        }
      }

      // Otherwise, report the event as usual
      return event
    }
  })

  const missingConfig = missingConnectionConfig(config)
  if (missingConfig.length) {
    Sentry.captureMessage(`Runtime config missing ${missingConfig.join(', ')} (stale or absent /config.js)`, 'error')
  }
}

const App = () => {
  return <RouterProvider router={router} />
}

const container = document.getElementById('root')
if (!container) throw new Error('Root element not found')
const root = createRoot(container)
root.render(<App />)

if (import.meta.hot) {
  import.meta.hot.accept()
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
